<template>
  <div>
    <h1 class="headline">{{ headerText }}</h1>
    <p>{{ snippet }}</p>

    <div class="mdl-card preview-card mdl-shadow--1dp">
      <transition name="expand">
        <div class="proxy-review-container">
          <div
            :class="
              isCustomType ? 'proxy-form-review-custom' : 'proxy-form-review'
            "
            v-if="isLoaded"
            v-html="confirmResponse.data"
          />
        </div>
      </transition>

      <transition name="expand">
        <div v-if="isLoading" class="App-confirm__event-card--loading">
          <h4>Loading...</h4>
          <div class="mdl-spinner mdl-js-spinner is-active"></div>
        </div>
      </transition>

      <transition name="expand">
        <div v-if="isError" class="App-confirm__event-card--error">
          <h4>Error loading, please try again</h4>
          <button
            @click="reload"
            class="mdl-button mdl-js-button mdl-button--primary mdl-button--raised"
          >
            Try again
          </button>
        </div>
      </transition>
    </div>

    <div v-if="isCollectSignature">
      <p class="proxy-signature">
        Before submitting your form, please sign the document by typing your
        name below.
      </p>
      <signature-pad :underline="!isBill106" @update="updateSignature" />
    </div>

    <div class="App-buttons">
      <button
        @click="backButtonClick"
        class="mdl-button mdl-js-button mdl-button--raised"
      >
        Go Back
      </button>
      <button
        @click="handleClick"
        :disabled="!this.isLoaded || isDisabled"
        class="mdl-button mdl-button--primary mdl-js-button mdl-button--raised"
      >
        Submit
      </button>
    </div>

    <p class="App-confirm__legal-text">
      <i v-if="isCollectSignature">
        By signing your name and clicking 'Submit', you are agreeing to the
        <a @click="termsClick">terms &amp; conditions</a>
        and acknowledge that you are submitting this form as though it were
        signed and delivered by hand.
      </i>
      <i v-else>
        By clicking 'Submit', you are agreeing to the
        <a @click="termsClick">terms &amp; conditions</a>
        and acknowledge that you are submitting this form as though it were
        signed and delivered by hand.
      </i>
    </p>

    <!-- use the modal component, pass in the prop -->
    <modal v-show="showModal" @close-modal="closeModal" />
  </div>
</template>

<script>
import _ from 'lodash';
import Modal from './TCSModal/TCSModal.vue';
import SignaturePad from '@/components/SignaturePad';
import {mapGetters, mapActions} from 'vuex';

const DEFAULT_HEADER = 'The Legal Stuff';
const DEFAULT_SNIPPET =
  'Please review the sample form below before submitting. You may need to use the scrollbar to view the entire document. A copy will be emailed to you upon submission. To make changes, click the "Go Back" button.';

export default {
  components: {
    Modal,
    SignaturePad
  },
  computed: {
    ...mapGetters('confirm', ['confirmResponse']),
    ...mapGetters(['displayOptions']),
    ...mapGetters(['meetingOptions']),
    ...mapGetters(['customSignature']),
    isLoading() {
      return this.confirmResponse.status === 0;
    },
    isLoaded() {
      return this.confirmResponse.status === 200;
    },
    isError() {
      return this.confirmResponse.status >= 400;
    },
    headerText() {
      const text = _.get(this.displayOptions, 'confirm.header_text', '');
      return text || DEFAULT_HEADER;
    },
    snippet() {
      const text = _.get(this.displayOptions, 'confirm.snippet', '');
      return text || DEFAULT_SNIPPET;
    },
    isCustomType() {
      const type = _.get(this.meetingOptions, 'proxy_template', '');
      return type === 'custom' || type === 'alberta';
    },
    isCollectSignature() {
      const collectSignature = _.get(
        this.meetingOptions,
        'collect_advance_ballot_signature',
        false
      );
      return collectSignature;
    },
    isBill106() {
      const template = _.get(this.meetingOptions, 'proxy_template', '');
      return template === 'on-bill-106';
    },
    isPreviewEnabled() {
      const {options} = this.currentMeeting;

      return options.enableProxyPreview || false;
    },
    isDisabled() {
      // Determine whether to disable the SUBMIT button or not
      if (!this.isCollectSignature) {
        return false;
      }
      return !this.customSignature;
    }
  },
  methods: {
    ...mapActions('confirm', ['getProxyContent', 'resetProxyContent']),
    ...mapActions('proxy', ['setProxyAbstainOption']),
    ...mapActions(['setCustomSignature']),
    handleClick() {
      // Track it
      this.$router.push({name: 'done'});
    },
    backButtonClick() {
      // Reset the abstain-all confirmation when returning to review page
      this.setProxyAbstainOption('');
      this.$router.push({name: 'review'});
    },
    closeModal() {
      this.showModal = false;
    },
    termsClick() {
      this.showModal = true;
    },
    reload() {
      this.resetProxyContent();
      this.getProxyContent();
    },
    updateSignature(signature) {
      this.setCustomSignature(signature);
    }
  },
  created() {
    // Get the content for user to review
    this.reload();
  },
  mounted() {
    componentHandler.upgradeAllRegistered();
  },
  data() {
    return {
      showModal: false,
      signature: ''
    };
  }
};
</script>

<style lang="scss">
$review-font-size: 12px;
$review-line-height: 1.45;
$review-left-margin: 25px;

.App-confirm__legal-text {
  margin-top: 2rem;
}
.App-confirm__event-card .expand-transition {
  background-color: #fff;
}
.App-confirm--text,
.App-confirm--text p {
  font-size: $review-font-size;
  line-height: $review-line-height;
}
/* This stuff comes from the API */
.review__details--item {
  display: block;
}
.review__list li {
  margin-bottom: 1rem;
  font-size: $review-font-size;
  line-height: $review-line-height;
}
.proxy--list-item .bylaw-vote,
.proxy--list-item .candidate {
  padding-left: 18px;
  padding-top: 6px;
}
.candidate,
.bylaw-vote {
  font-weight: bold;
}
.App-confirm__event-card--error,
.App-confirm__event-card--loading {
  text-align: center;
}
.preview-card {
  width: 100%;
  border: 1px solid #e0e0e0;
}
.proxy-review-container {
  max-height: 400px;
  overflow-y: scroll;
}
.proxy-signature {
  margin-top: 10px;
}

@media only screen and (max-width: 450px) {
  .proxy-review-container {
    max-height: 40vh;
  }
}

/* Styles for the review HTML */
.proxy-form-review {
  padding: 20px 20px 20px 20px;

  .flex-right-side {
    display: none;
  }

  .flex-left-side {
    flex: 0 1 100%;
  }

  .election-title.short-box {
    width: 250px;
  }

  @media only screen and (max-width: 699px) {
    zoom: 40%;

    .election-order {
      width: 20%;
    }

    .election-title {
      width: 45%;
      padding-left: 4px;
    }

    .election-checkbox {
      width: 20%;
    }

    .election-title.short-box {
      width: 35%;
      padding-left: 0px;
    }

    .bylaw-title {
      width: 60%;
      padding-left: 0px;
    }

    .bylaw-title.answer-input {
      border-bottom: 1px solid black;
    }

    // .bylaw-checkbox {
    //   width: 20%;
    // }
  }

  header {
    margin-bottom: 30px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 12px;
    line-height: 1.3;
    text-align: center;
    margin-top: 10px;
    margin-bottom: 10px;
  }

  p,
  li {
    font-size: 12px;
    line-height: 1.4;
  }

  ol {
    padding-left: 30px;
  }

  li {
    margin-top: 10px;
    margin-bottom: 10px;
  }

  .answer-text {
    margin-left: $review-left-margin;
    font-weight: bold;
    text-decoration: underline;
    display: block;
  }

  .answer-text--election {
    text-transform: uppercase;
  }

  .election-question-header {
    font-weight: 600;
  }

  .proxy-signature {
    margin-left: 15px;
    margin-top: 30px;
    margin-bottom: 30px;
  }

  .proxy-signature p {
    line-height: 1.5;
  }

  .proxy-footer {
    opacity: 0.87;
    font-style: italic;
  }

  .proxy-logo {
    height: 120px;
  }

  .proxy-id-box {
    display: none;
  }
}

/* Styles for the review HTML */
.proxy-form-review-custom {
  padding: 20px 20px 20px 20px;
  font-size: 12px;
  font-family: sans-serif;

  .election-title.short-box {
    width: 250px;
  }
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.17em;
  }
  h4 {
    font-size: 1em;
    text-align: center;
  }
  h1,
  h2,
  h3,
  h4 {
    line-height: 1.3;
    font-weight: bold;
    margin-top: 5px;
    margin-bottom: 5px;
  }

  ol {
    margin-left: 0px;
  }

  a {
    color: blue;
  }

  li,
  p {
    font-size: 12px;
  }
  li {
    margin-top: 15px;
    margin-bottom: 15px;
    padding-left: 10px;
  }
  .answer-text {
    margin-left: $review-left-margin;
    margin-top: 20px;
    margin-bottom: 20px;
    font-weight: bold;
    text-decoration: underline;
    display: block;
  }

  .answer-text--election {
    text-transform: uppercase;
  }

  .election-question-header {
    font-weight: 600;
  }
  .bylaw-vote {
    margin-left: 45px;
    margin-top: 20px;
    margin-bottom: 20px;
    font-weight: bold;
    text-decoration: underline;
  }
}
</style>
